import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Check,
  ChevronDown,
  ExternalLink,
  Eye,
  Image,
  Languages,
  LayoutGrid,
  Loader2,
  Move,
  Palette,
  RotateCcw,
  Save,
  Search,
  SlidersHorizontal,
  Sparkles,
  Type,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  menusApi,
  menuTemplatesApi,
} from '../../api';

import PublicMenuExperience from '../../components/menu/PublicMenuExperience';

import { useAlerts } from '../../components/feedback/AlertProvider';

import { useLanguage } from '../../contexts/LanguageContext';

import translateApiError from '../../utils/translateApiError';

import '../../styles/Menus/menu-customize.css';

const DEFAULT_ESTABLISHMENT_ID = 1;

const PREVIEW_PHONE_WIDTH = 390;

const PREVIEW_PHONE_HEIGHT = 760;

const SUPPORTED_LOCALES = ['fr', 'ar', 'en', 'es', 'de', 'zh', 'ru', 'nl', 'ja', 'it', 'pt', 'tr'];
const LANGUAGE_NAMES = {
  fr: 'Français', ar: 'العربية', en: 'English', es: 'Español',
  de: 'Deutsch', zh: '中文', ru: 'Русский', nl: 'Nederlands',
  ja: '日本語', it: 'Italiano', pt: 'Português', tr: 'Türkçe',
};

// The same compatibility rules used by MenuForm: older menus show all languages.
const readEnabledLanguages = (menu) => {
  const raw = menu?.enabled_languages ?? menu?.selected_languages ??
    menu?.available_languages ?? menu?.settings?.enabled_languages ??
    menu?.settings?.selected_languages ?? menu?.settings?.available_languages;
  if (raw === undefined || raw === null || raw === '') return [...SUPPORTED_LOCALES];
  let languages = raw;
  if (typeof languages === 'string') {
    try { languages = JSON.parse(languages); }
    catch { languages = languages.split(',').map((locale) => locale.trim()); }
  }
  if (languages && !Array.isArray(languages) && typeof languages === 'object') {
    languages = Object.keys(languages).filter((locale) => Boolean(languages[locale]));
  }
  if (!Array.isArray(languages)) return [...SUPPORTED_LOCALES];
  const selected = new Set(languages);
  const defaultLanguage = SUPPORTED_LOCALES.includes(menu?.default_language)
    ? menu.default_language : 'en';
  selected.add(defaultLanguage);
  return SUPPORTED_LOCALES.filter((locale) => selected.has(locale));
};

const DEFAULT_CUSTOM_SETTINGS = {
  layout: '',
  theme: '',
  card_shadow: '',
  primary_color: '',
  background_color: '',
  text_color: '',
  surface_color: '',
  accent_color: '',
  show_images: null,
  show_search: null,
  show_category_tabs: null,
  show_category_sections: null,
  show_subcategories: null,
  show_featured_section: null,
  show_product_description: null,
  show_engagement: null,
  show_restaurant_info: null,
  show_ordering_badge: null,
  ordering_enabled: null,
  product_density: '',
  border_radius: '',
  card_style: '',
  product_image_ratio: '',
  category_style: '',
  logo_position: '',
  logo_size: '',
  header_alignment: '',
  header_layout: '',
  font_family: '',
  heading_scale: '',
  body_scale: '',
  content_width: '',
  section_gap: '',
  section_order: [],
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (value && typeof value === 'object') {
    return Object.values(value);
  }
  return [];
};

const normalizeTranslations = (translations) => {
  if (Array.isArray(translations)) {
    return translations.filter(Boolean);
  }
  if (translations && typeof translations === 'object') {
    return Object.entries(translations).map(
      ([locale, translation]) => {
        if (!translation || typeof translation !== 'object') {
          return {
            locale,
            name: translation ? String(translation) : '',
          };
        }
        return {
          locale: translation.locale || locale,
          ...translation,
        };
      },
    );
  }
  return [];
};

const getTranslationValue = (
  item,
  field,
  language,
  fallback = 'fr',
) => {
  if (!item) return '';
  if (item?.translations?.[language]?.[field]) {
    return item.translations[language][field];
  }
  if (item?.translations?.[fallback]?.[field]) {
    return item.translations[fallback][field];
  }
  const translations = normalizeTranslations(
    item.translations,
  );
  const selectedTranslation =
    translations.find(
      (translation) => translation.locale === language,
    ) ||
    translations.find(
      (translation) => translation.locale === fallback,
    ) ||
    translations.find(
      (translation) => translation.locale === 'en',
    ) ||
    translations.find(
      (translation) => translation.locale === 'ar',
    ) ||
    translations.find(
      (translation) => translation.locale === 'es',
    ) ||
    translations[0];
  return (
    selectedTranslation?.[field] ||
    item?.[field] ||
    ''
  );
};

const DEFAULT_SECTION_ORDER = [
  'featured',
  'categories',
  'products',
];

const getInitialCustomSettings = (settings = {}) => {
  const normalized = Object.keys(DEFAULT_CUSTOM_SETTINGS).reduce(
    (result, key) => ({
      ...result,
      [key]:
        settings[key] === undefined
          ? DEFAULT_CUSTOM_SETTINGS[key]
          : settings[key],
    }),
    {},
  );
  normalized.section_order =
    Array.isArray(settings.section_order) &&
    settings.section_order.length > 0
      ? settings.section_order
      : DEFAULT_SECTION_ORDER;
  return normalized;
};

const getCustomOverrides = (settings = {}) => {
  return Object.fromEntries(
    Object.entries(settings).filter(
      ([, value]) =>
        value !== '' &&
        value !== null &&
        value !== undefined,
    ),
  );
};

const translateFallback = (
  t,
  key,
  fallback,
) => {
  const value = t(key);
  return value === key ? fallback : value;
};

const normalizeSearchText = (value = '') => {
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const getTemplateSearchText = (template) => {
  const translationValues = normalizeTranslations(
    template?.translations,
  ).flatMap((translation) => [
    translation?.name,
    translation?.description,
  ]);
  return normalizeSearchText(
    [
      template?.name,
      template?.description,
      template?.slug,
      template?.source,
      template?.config?.layout,
      template?.config?.theme,
      template?.config?.card_style,
      template?.config?.font_family,
      ...translationValues,
    ]
      .filter(Boolean)
      .join(' '),
  );
};

const templateMatchesSearch = (template, query) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return true;
  const searchableText = getTemplateSearchText(template);
  const words = searchableText.split(' ').filter(Boolean);
  const queryTokens = normalizedQuery.split(' ').filter(Boolean);
  return queryTokens.every((token) => {
    if (searchableText.includes(token)) {
      return true;
    }
    // Small typo tolerance without requiring the exact template name.
    return words.some((word) => {
      if (word.startsWith(token) || token.startsWith(word)) {
        return true;
      }
      if (token.length < 4 || word.length < 4) {
        return false;
      }
      let mismatches = 0;
      const minLength = Math.min(token.length, word.length);
      for (let index = 0; index < minLength; index += 1) {
        if (token[index] !== word[index]) {
          mismatches += 1;
        }
        if (mismatches > 1) {
          return false;
        }
      }
      return Math.abs(token.length - word.length) <= 1;
    });
  });
};

function CustomSelect({
  value,
  options,
  onChange,
  label,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = options.find((option) => option.value === value) || options[0];
  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);
  return (
    <div ref={rootRef} className="menu-customize-custom-select">
      <button
        type="button"
        className="menu-customize-custom-select__trigger"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
      >
        <span>{selected?.label || value}</span>
        <ChevronDown size={15} className={open ? 'is-open' : ''} />
      </button>
      {open && (
        <div className="menu-customize-custom-select__menu">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={option.value === value ? 'is-selected' : ''}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              <span>{option.label}</span>
              {option.value === value && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MenuCustomize() {
  const { menuId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { showAlert } = useAlerts();
  const [menu, setMenu] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] =
    useState('');
  const [customSettings, setCustomSettings] =
    useState(DEFAULT_CUSTOM_SETTINGS);
  const [previewSearchTerm, setPreviewSearchTerm] =
    useState('');
  const [previewLanguage, setPreviewLanguage] =
    useState('en');
  const [activePanel, setActivePanel] =
    useState('templates');
  const [previewZoom, setPreviewZoom] =
    useState(0.7);
  const [isPreviewZoomManual, setIsPreviewZoomManual] =
    useState(false);
  const previewStageRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [establishmentSlug, setEstablishmentSlug] = useState('');
  const establishmentId =
    localStorage.getItem(
      'selected_establishment_id',
    ) || DEFAULT_ESTABLISHMENT_ID;
  const selectedTemplate = useMemo(() => {
    return templates.find(
      (template) =>
        Number(template.id) ===
        Number(selectedTemplateId),
    );
  }, [templates, selectedTemplateId]);
  const filteredTemplates = useMemo(() => {
    return templates.filter((template) =>
      templateMatchesSearch(
        template,
        templateSearchTerm,
      ),
    );
  }, [templates, templateSearchTerm]);
  const enabledLanguages = useMemo(() => {
    // Prefer the actual menu record over the preview snapshot.
    const source = menu || previewData?.menu;
    return readEnabledLanguages(source);
  }, [menu, previewData?.menu]);
  useEffect(() => {
    if (!enabledLanguages.includes(previewLanguage)) {
      setPreviewLanguage(enabledLanguages.includes(menu?.default_language)
        ? menu.default_language : enabledLanguages[0] || 'en');
    }
  }, [enabledLanguages, previewLanguage, menu?.default_language]);
  const activeTemplate =
    selectedTemplate ||
    previewData?.template ||
    menu?.menu_template ||
    menu?.template ||
    null;
  const templateConfig =
    activeTemplate?.config || {};
  const effectiveConfig = useMemo(
    () => ({
      ...templateConfig,
      ...getCustomOverrides(
        customSettings,
      ),
    }),
    [
      templateConfig,
      customSettings,
    ],
  );
  const establishment =
    previewData?.establishment || {};
  const publicEstablishmentSlug =
    establishment?.slug || menu?.establishment?.slug || menu?.establishment_slug || establishmentSlug;
  const categories = toArray(
    previewData?.categories,
  );
  const products = toArray(
    previewData?.products,
  );
  const featuredProducts = toArray(
    previewData?.featured_products,
  );
  const baseOrderingEnabled = Boolean(
    previewData?.menu?.ordering_enabled ??
      previewData?.menu?.settings
        ?.ordering_enabled ??
      previewData?.establishment
        ?.ordering_enabled ??
      false,
  );
  const previewOrderingEnabled =
    customSettings.ordering_enabled ??
    templateConfig.ordering_enabled ??
    baseOrderingEnabled;
  const getBooleanSettingValue = (
    key,
    defaultValue = true,
  ) => {
    return (
      customSettings[key] ??
      templateConfig[key] ??
      defaultValue
    );
  };
  const updateCustomSetting = (
    key,
    value,
  ) => {
    setCustomSettings(
      (currentSettings) => ({
        ...currentSettings,
        [key]: value,
      }),
    );
  };
  const resetCustomSettings = () => {
    setCustomSettings({
      ...DEFAULT_CUSTOM_SETTINGS,
      section_order: DEFAULT_SECTION_ORDER,
    });
  };
  const fitPreviewToCanvas = useCallback(() => {
    const previewStage = previewStageRef.current;
    if (!previewStage) {
      return;
    }
    const { width, height } =
      previewStage.getBoundingClientRect();
    if (!width || !height) {
      return;
    }
    /*
     * Base phone preview size:
     * 375px wide x 780px high.
     *
     * Keep small canvas padding, then choose the largest
     * zoom that still shows the complete phone.
     */
    const availableWidth = Math.max(width - 56, 260);
    const availableHeight = Math.max(height - 64, 360);
    const nextZoom = Math.min(
      availableWidth / PREVIEW_PHONE_WIDTH,
      availableHeight / PREVIEW_PHONE_HEIGHT,
      1.12,
    );
    setPreviewZoom(
      Number(Math.max(0.28, nextZoom).toFixed(2)),
    );
  }, []);
  const scrollPhonePreview = useCallback((event) => {
    const previewStage = previewStageRef.current;
    if (!previewStage) {
      return false;
    }
    const scrollRegion =
      previewStage.querySelector(
        '.menu-customize-public-phone .public-menu--phone-preview > .public-menu__scroll-region',
      ) ||
      previewStage.querySelector(
        '.menu-customize-public-phone .public-menu--phone-preview',
      );
    if (!scrollRegion) {
      return false;
    }
    const maxScrollTop =
      scrollRegion.scrollHeight - scrollRegion.clientHeight;
    if (maxScrollTop <= 0) {
      return false;
    }
    const nextScrollTop = Math.min(
      maxScrollTop,
      Math.max(0, scrollRegion.scrollTop + event.deltaY),
    );
    if (nextScrollTop === scrollRegion.scrollTop) {
      return false;
    }
    event.preventDefault();
    event.stopPropagation();
    scrollRegion.scrollTop = nextScrollTop;
    return true;
  }, []);
  useEffect(() => {
    const handleGlobalWheel = (event) => {
      /*
       * Completely disable the browser's native Ctrl/Cmd + wheel
       * page zoom while the customization page is mounted.
       */
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const previewStage = previewStageRef.current;
        /*
         * Only change the phone zoom when the mouse is actually
         * inside the preview canvas. Outside the preview canvas,
         * browser zoom is still blocked but nothing else happens.
         */
        if (
          !previewStage ||
          !previewStage.contains(event.target)
        ) {
          return;
        }
        const direction =
          event.deltaY > 0 ? -1 : 1;
        setIsPreviewZoomManual(true);
        setPreviewZoom((currentZoom) => {
          const nextZoom =
            currentZoom +
            direction * 0.05;
          return Number(
            Math.min(
              1.25,
              Math.max(
                0.28,
                nextZoom,
              ),
            ).toFixed(2),
          );
        });
        return;
      }
      const previewStage = previewStageRef.current;
      if (
        !previewStage ||
        !previewStage.contains(event.target)
      ) {
        return;
      }
      const isInsidePhone = Boolean(
        event.target.closest(
          '.menu-customize-public-phone',
        ),
      );
      /*
       * Normal wheel inside the phone scrolls only the menu.
       */
      if (isInsidePhone) {
        scrollPhonePreview(event);
      }
      /*
       * Normal wheel outside the phone is untouched so the
       * preview stage can scroll/pan the phone up and down.
       */
    };
    /*
     * Capture at document level so Chrome/Edge never gets a chance
     * to apply its own page zoom first.
     */
    document.addEventListener(
      'wheel',
      handleGlobalWheel,
      {
        passive: false,
        capture: true,
      },
    );
    return () => {
      document.removeEventListener(
        'wheel',
        handleGlobalWheel,
        {
          capture: true,
        },
      );
    };
  }, [scrollPhonePreview]);
  const moveSection = (section, direction) => {
    setCustomSettings((currentSettings) => {
      const order = Array.isArray(currentSettings.section_order) &&
        currentSettings.section_order.length > 0
        ? [...currentSettings.section_order]
        : [...DEFAULT_SECTION_ORDER];
      const index = order.indexOf(section);
      const nextIndex = direction === 'up' ? index - 1 : index + 1;
      if (index < 0 || nextIndex < 0 || nextIndex >= order.length) {
        return currentSettings;
      }
      [order[index], order[nextIndex]] = [order[nextIndex], order[index]];
      return {
        ...currentSettings,
        section_order: order,
      };
    });
  };
  const loadData = async () => {
    try {
      setLoading(true);
      const [
        menuResponse,
        templatesResponse,
        previewResponse,
      ] = await Promise.all([
        menusApi.getMenu(menuId),
        menuTemplatesApi.getTemplates({
          per_page: 100,
        }),
        menuTemplatesApi.getMenuPreview(
          establishmentId,
          menuId,
        ),
      ]);
      const currentMenu =
        menuResponse.data?.data || null;
      const templatesList =
        templatesResponse.data?.data?.data ||
        templatesResponse.data?.data ||
        [];
      const preview =
        previewResponse.data?.data || null;
      if (!currentMenu) {
        throw new Error(
          'Menu not found.',
        );
      }
      setMenu(currentMenu);
      setTemplates(
        Array.isArray(templatesList)
          ? templatesList
          : [],
      );
      setPreviewData(preview);
      const slugFromData = preview?.establishment?.slug || currentMenu?.establishment?.slug || currentMenu?.establishment_slug;
      if (slugFromData) {
        setEstablishmentSlug(slugFromData);
      } else {
        try {
          const shareResponse = await menuTemplatesApi.getMenuShare(establishmentId, menuId);
          setEstablishmentSlug(shareResponse.data?.data?.establishment_slug || '');
        } catch {
          setEstablishmentSlug('');
        }
      }
      const currentSettings =
        preview?.menu?.design_settings ||
        preview?.menu
          ?.menu_design_settings ||
        currentMenu?.design_settings ||
        currentMenu
          ?.menu_design_settings ||
        {};
      setCustomSettings(
        getInitialCustomSettings(
          currentSettings,
        ),
      );
      const currentTemplateId =
        preview?.template?.id ||
        preview?.menu
          ?.menu_template_id ||
        currentMenu
          ?.menu_template_id ||
        currentMenu?.menu_template?.id ||
        currentMenu?.template?.id ||
        templatesList?.[0]?.id ||
        '';
      setSelectedTemplateId(
        currentTemplateId
          ? String(
              currentTemplateId,
            )
          : '',
      );
      setPreviewLanguage(
        preview?.menu?.default_language ||
          currentMenu?.default_language ||
          'en',
      );
      localStorage.setItem(
        'selected_menu_id',
        String(currentMenu.id),
      );
    } catch (error) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translateApiError(
          error,
          t,
        ),
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadData();
  }, [menuId]);
  useEffect(() => {
    const previewStage = previewStageRef.current;
    if (!previewStage) {
      return undefined;
    }
    if (!isPreviewZoomManual) {
      requestAnimationFrame(fitPreviewToCanvas);
      window.setTimeout(fitPreviewToCanvas, 80);
    }
    const resizeObserver = new ResizeObserver(() => {
      if (!isPreviewZoomManual) {
        requestAnimationFrame(fitPreviewToCanvas);
      }
    });
    resizeObserver.observe(previewStage);
    return () => {
      resizeObserver.disconnect();
    };
  }, [
    fitPreviewToCanvas,
    isPreviewZoomManual,
    activePanel,
    loading,
  ]);
  const handleSave = async () => {
    if (!selectedTemplateId) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translateFallback(
          t,
          'menuDesign.alerts.selectTemplateFirst',
          'Select a template first.',
        ),
      });
      return;
    }
    try {
      setSaving(true);
      await menuTemplatesApi.selectTemplate(
        establishmentId,
        {
          menu_id: Number(menuId),
          menu_template_id: Number(
            selectedTemplateId,
          ),
          menu_design_settings: {
            ...getCustomOverrides(
              customSettings,
            ),
            selected_at:
              new Date().toISOString(),
          },
        },
        menuId,
      );
      const previewResponse =
        await menuTemplatesApi.getMenuPreview(
          establishmentId,
          menuId,
        );
      const nextPreview =
        previewResponse.data?.data || null;
      setPreviewData(nextPreview);
      const nextSettings =
        nextPreview?.menu
          ?.design_settings ||
        nextPreview?.menu
          ?.menu_design_settings ||
        customSettings;
      setCustomSettings(
        getInitialCustomSettings(
          nextSettings,
        ),
      );
      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: translateFallback(
          t,
          'menus.customize.saved',
          'Menu customization saved successfully.',
        ),
      });
    } catch (error) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: translateApiError(
          error,
          t,
        ),
      });
    } finally {
      setSaving(false);
    }
  };
  if (loading) {
    return (
      <div className="menu-customize-page">
        <div className="menu-customize-loading">
          <Loader2 size={38} />
          <h2>
            {translateFallback(
              t,
              'menus.customize.loading',
              'Loading menu designer...',
            )}
          </h2>
        </div>
      </div>
    );
  }
  if (!menu) {
    return (
      <div className="menu-customize-page">
        <div className="menu-customize-empty">
          <Palette size={34} />
          <h2>
            {translateFallback(
              t,
              'menus.customize.notFound',
              'Menu not found.',
            )}
          </h2>
          <Link
            to="/menus"
            className="menu-customize-primary-button"
          >
            {translateFallback(
              t,
              'menus.customize.backToMenus',
              'Back to menus',
            )}
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className="menu-customize-page">
      <header className="menu-customize-header">
        <div className="menu-customize-header__left">
          <button
            type="button"
            className="menu-customize-icon-button"
            onClick={() =>
              navigate(
                `/menus/${menuId}/edit`,
              )
            }
            aria-label={t('common.back')}
          >
            <ArrowLeft size={19} />
          </button>
          <div className="menu-customize-header__identity">
            <p>
              {translateFallback(
                t,
                'menus.customize.eyebrow',
                'Menu customization',
              )}
            </p>
            <h1>
              {menu.name}
            </h1>
            <small>
              {translateFallback(
                t,
                'menus.customize.description',
                'Customize the visual appearance of this menu.',
              )}
            </small>
          </div>
        </div>
        <div className="menu-customize-header__actions">
          {menu.is_active &&
            menu.slug && (
              <Link
                to={publicEstablishmentSlug ? `/${encodeURIComponent(publicEstablishmentSlug)}/${encodeURIComponent(menu.slug)}` : "#"}
                onClick={(event) => {
                  if (!publicEstablishmentSlug) {
                    event.preventDefault();
                    showAlert({ type: 'error', title: t('alerts.error'), message: 'Unable to retrieve the establishment slug.' });
                  }
                }}
                target="_blank"
                rel="noreferrer"
                className="menu-customize-secondary-button"
              >
                <ExternalLink
                  size={17}
                />
                {translateFallback(
                  t,
                  'menus.customize.openPublicMenu',
                  'Open public menu',
                )}
              </Link>
            )}
          <button
            type="button"
            className="menu-customize-primary-button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <Loader2 size={18} />
            ) : (
              <Save size={18} />
            )}
            {saving
              ? t('common.saving')
              : translateFallback(
                  t,
                  'menus.customize.save',
                  'Save design',
                )}
          </button>
        </div>
      </header>
      <main className="menu-customize-workspace">
        <aside
          className={`menu-customize-sidebar ${
            !activePanel ? 'is-collapsed' : ''
          }`}
        >
          <nav className="menu-customize-tool-tabs">
            {[
              ['templates', Sparkles, t('menus.customize.tabs.templates')],
              ['layout', Move, t('menus.customize.tabs.layout')],
              ['style', Palette, t('menus.customize.tabs.style')],
              ['type', Type, t('menus.customize.tabs.type')],
              ['language', Languages, t('menus.customize.tabs.language')],
              ['elements', Eye, t('menus.customize.tabs.elements')],
            ].map(([id, Icon, label]) => (
              <button
                key={id}
                type="button"
                className={activePanel === id ? 'is-active' : ''}
                onClick={() =>
                  setActivePanel((currentPanel) =>
                    currentPanel === id ? '' : id,
                  )
                }
                title={label}
                aria-pressed={activePanel === id}
                aria-expanded={activePanel === id}
              >
                <span className="menu-customize-tool-tabs__icon">
                  <Icon size={19} />
                </span>
                <span>{label}</span>
              </button>
            ))}
          </nav>
          {activePanel && (
            <div className="menu-customize-sidebar__panel">
          {activePanel === 'templates' && (
            <section className="menu-customize-panel">
              <div className="menu-customize-panel__title">
                <span><Sparkles size={17} /></span>
                <div>
                  <h2>{t('menus.customize.tabs.templates')}</h2>
                  <p>{t('menus.customize.templates.description')}</p>
                </div>
              </div>
              <label className="menu-customize-preview-filter">
                <Search size={16} />
                <input
                  type="search"
                  value={templateSearchTerm}
                  placeholder={translateFallback(
                    t,
                    'menus.customize.templates.searchPlaceholder',
                    'Search templates...',
                  )}
                  onChange={(event) =>
                    setTemplateSearchTerm(event.target.value)
                  }
                />
              </label>
              <div className="menu-customize-template-list">
                {filteredTemplates.length > 0 ? (
                  filteredTemplates.map((template) => {
                    const isSelected =
                      Number(template.id) ===
                      Number(selectedTemplateId);
                    return (
                      <button
                        key={template.id}
                        type="button"
                        className={`menu-customize-template-card ${isSelected ? 'is-selected' : ''}`}
                        onClick={() =>
                          setSelectedTemplateId(String(template.id))
                        }
                      >
                        <span className="menu-customize-template-card__icon">
                          <LayoutGrid size={18} />
                        </span>
                        <span className="menu-customize-template-card__content">
                          <strong>
                            {getTranslationValue(
                              template,
                              'name',
                              language,
                            )}
                          </strong>
                          <small>
                            {getTranslationValue(
                              template,
                              'description',
                              language,
                            )}
                          </small>
                        </span>
                        {isSelected && (
                          <span className="menu-customize-template-card__selected">
                            <Check size={15} />
                          </span>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="menu-customize-empty">
                    <Search size={24} />
                    <p>
                      {translateFallback(
                        t,
                        'menus.customize.templates.noSearchResults',
                        'No matching templates found.',
                      )}
                    </p>
                  </div>
                )}
              </div>
            </section>
          )}
          {activePanel === 'layout' && (
            <>
              <section className="menu-customize-panel">
                <div className="menu-customize-panel__title">
                  <span><LayoutGrid size={17} /></span>
                  <div>
                    <h2>
                      {translateFallback(
                        t,
                        'menus.customize.layout.menuLayout',
                        'Menu layout',
                      )}
                    </h2>
                    <p>
                      {translateFallback(
                        t,
                        'menus.customize.layout.menuLayoutDescription',
                        'Choose the main product presentation used by the public menu.',
                      )}
                    </p>
                  </div>
                </div>
                <label className="menu-customize-field">
                  <span>
                    {translateFallback(
                      t,
                      'menus.customize.layout.layoutStyle',
                      'Layout style',
                    )}
                  </span>
                  <CustomSelect
                    value={
                      customSettings.layout ||
                      templateConfig.layout ||
                      'classic'
                    }
                    options={[
                      {
                        value: 'classic',
                        label: translateFallback(
                          t,
                          'menus.customize.layout.layoutClassic',
                          'Classic',
                        ),
                      },
                      {
                        value: 'premium-square-grid',
                        label: translateFallback(
                          t,
                          'menus.customize.layout.layoutPremiumSquare',
                          'Premium square grid',
                        ),
                      },
                      {
                        value: 'golden-burger-grid',
                        label: translateFallback(
                          t,
                          'menus.customize.layout.layoutGoldenBurger',
                          'Golden burger grid',
                        ),
                      },
                      {
                        value: 'green-promo-cards',
                        label: translateFallback(
                          t,
                          'menus.customize.layout.layoutGreenPromo',
                          'Green promo cards',
                        ),
                      },
                      {
                        value: 'twilight-tavern',
                        label: translateFallback(
                          t,
                          'menus.customize.layout.layoutTwilight',
                          'Twilight tavern',
                        ),
                      },
                      {
                        value: 'dark-sushi-phone',
                        label: translateFallback(
                          t,
                          'menus.customize.layout.layoutDarkSushi',
                          'Dark sushi',
                        ),
                      },
                    ]}
                    onChange={(value) =>
                      updateCustomSetting('layout', value)
                    }
                  />
                </label>
              </section>
              <section className="menu-customize-panel">
                <div className="menu-customize-panel__title">
                  <span><Move size={17} /></span>
                  <div>
                    <h2>{t('menus.customize.layout.logoPlacement')}</h2>
                    <p>{t('menus.customize.layout.logoPlacementDescription')}</p>
                  </div>
                </div>
                <div className="menu-customize-position-grid">
                  {[
                    ['top-left', t('menus.customize.layout.topLeft')],
                    ['top-center', t('menus.customize.layout.topCenter')],
                    ['top-right', t('menus.customize.layout.topRight')],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={(customSettings.logo_position || templateConfig.logo_position || 'top-right') === value ? 'is-active' : ''}
                      onClick={() => updateCustomSetting('logo_position', value)}
                    >
                      <span className={`position-dot position-dot--${value}`} />
                      <small>{label}</small>
                    </button>
                  ))}
                </div>
                <label className="menu-customize-range">
                  <span>{t('menus.customize.layout.logoSize')} <b>{customSettings.logo_size || templateConfig.logo_size || 72}px</b></span>
                  <input
                    type="range"
                    min="42"
                    max="140"
                    step="2"
                    value={customSettings.logo_size || templateConfig.logo_size || 72}
                    onChange={(event) => updateCustomSetting('logo_size', Number(event.target.value))}
                  />
                </label>
              </section>
              <section className="menu-customize-panel">
                <div className="menu-customize-panel__title">
                  <span><LayoutGrid size={17} /></span>
                  <div>
                    <h2>{t('menus.customize.layout.header')}</h2>
                    <p>{t('menus.customize.layout.headerDescription')}</p>
                  </div>
                </div>
                <div className="menu-customize-choice-grid">
  {[
    {
      value: "classic",
      label: t("menus.customize.layout.classic"),
    },
    {
      value: "centered",
      label: t("menus.customize.layout.centered"),
    },
    {
      value: "split",
      label: t("menus.customize.layout.split"),
    },
  ].map((option) => (
    <button
      key={option.value}
      type="button"
      className={
        (customSettings.header_layout ||
          templateConfig.header_layout ||
          "classic") === option.value
          ? "is-active"
          : ""
      }
      onClick={() =>
        updateCustomSetting(
          "header_layout",
          option.value
        )
      }
    >
      {option.label}
    </button>
  ))}
</div>
                <div className="menu-customize-align-buttons">
                  {[
                    ['left', AlignLeft],
                    ['center', AlignCenter],
                    ['right', AlignRight],
                  ].map(([value, Icon]) => (
                    <button
                      key={value}
                      type="button"
                      className={(customSettings.header_alignment || templateConfig.header_alignment || 'left') === value ? 'is-active' : ''}
                      onClick={() => updateCustomSetting('header_alignment', value)}
                    >
                      <Icon size={17} />
                    </button>
                  ))}
                </div>
              </section>
              <section className="menu-customize-panel">
                <div className="menu-customize-panel__title">
                  <span><SlidersHorizontal size={17} /></span>
                  <div>
                    <h2>{t('menus.customize.layout.spacing')}</h2>
                    <p>{t('menus.customize.layout.spacingDescription')}</p>
                  </div>
                </div>
                <label className="menu-customize-range">
                  <span>
                    {translateFallback(
                      t,
                      'menus.customize.layout.contentWidth',
                      'Content width',
                    )}{' '}
                    <b>{customSettings.content_width || templateConfig.content_width || 1080}px</b>
                  </span>
                  <input
                    type="range"
                    min="720"
                    max="1280"
                    step="20"
                    value={customSettings.content_width || templateConfig.content_width || 1080}
                    onChange={(event) =>
                      updateCustomSetting('content_width', Number(event.target.value))
                    }
                  />
                </label>
                <label className="menu-customize-range">
                  <span>{t('menus.customize.layout.sectionGap')} <b>{customSettings.section_gap || templateConfig.section_gap || 24}px</b></span>
                  <input type="range" min="8" max="56" step="2" value={customSettings.section_gap || templateConfig.section_gap || 24} onChange={(event) => updateCustomSetting('section_gap', Number(event.target.value))} />
                </label>
              </section>
              <section className="menu-customize-panel">
                <div className="menu-customize-panel__title">
                  <span><Move size={17} /></span>
                  <div>
                    <h2>{t('menus.customize.layout.sectionOrder')}</h2>
                    <p>{t('menus.customize.layout.sectionOrderDescription')}</p>
                  </div>
                </div>
                <div className="menu-customize-section-order">
                  {(customSettings.section_order?.length ? customSettings.section_order : DEFAULT_SECTION_ORDER).map((section, index, order) => (
                    <div key={section}>
                      <span>{index + 1}</span>
                      <strong>{section === 'featured' ? t('menus.customize.layout.specialDishes') : section === 'categories' ? t('menus.customize.layout.categories') : t('menus.customize.layout.products')}</strong>
                      <div>
                        <button type="button" disabled={index === 0} onClick={() => moveSection(section, 'up')}><ArrowUp size={15} /></button>
                        <button type="button" disabled={index === order.length - 1} onClick={() => moveSection(section, 'down')}><ArrowDown size={15} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}
          {activePanel === 'style' && (
            <>
              <section className="menu-customize-panel">
                <div className="menu-customize-panel__title">
                  <span><Palette size={17} /></span>
                  <div>
                    <h2>
                      {translateFallback(
                        t,
                        'menus.customize.style.theme',
                        'Theme',
                      )}
                    </h2>
                    <p>
                      {translateFallback(
                        t,
                        'menus.customize.style.themeDescription',
                        'Choose the overall light, dark, or warm appearance of the menu.',
                      )}
                    </p>
                  </div>
                </div>
                <div className="menu-customize-choice-grid">
                  {[
                    {
                      value: 'light',
                      label: translateFallback(
                        t,
                        'menus.customize.style.themeLight',
                        'Light',
                      ),
                    },
                    {
                      value: 'dark',
                      label: translateFallback(
                        t,
                        'menus.customize.style.themeDark',
                        'Dark',
                      ),
                    },
                    {
                      value: 'orange',
                      label: translateFallback(
                        t,
                        'menus.customize.style.themeWarm',
                        'Warm',
                      ),
                    },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={
                        (
                          customSettings.theme ||
                          templateConfig.theme ||
                          'light'
                        ) === option.value
                          ? 'is-active'
                          : ''
                      }
                      onClick={() =>
                        updateCustomSetting(
                          'theme',
                          option.value,
                        )
                      }
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </section>
              <section className="menu-customize-panel">
                <div className="menu-customize-panel__title">
                  <span><Palette size={17} /></span>
                  <div><h2>{t('menus.customize.style.brandColors')}</h2><p>{t('menus.customize.style.brandColorsDescription')}</p></div>
                </div>
                <div className="menu-customize-colors">
                  {[
                    ['primary_color', t('menus.customize.style.primary'), '#51766E'],
                    ['accent_color', t('menus.customize.style.accent'), '#D7E3E0'],
                    ['background_color', t('menus.customize.style.background'), '#F8FAF9'],
                    ['surface_color', t('menus.customize.style.cards'), '#FFFFFF'],
                    ['text_color', t('menus.customize.style.text'), '#333333'],
                  ].map(([key, label, fallback]) => {
                    const value = customSettings[key] || templateConfig[key] || fallback;
                    return (
                      <label key={key}>
                        <span>{label}</span>
                        <div className="menu-customize-color-input">
                          <input type="color" value={value} onChange={(event) => updateCustomSetting(key, event.target.value)} />
                          <code>{value}</code>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </section>
              <section className="menu-customize-panel">
                <div className="menu-customize-panel__title">
                  <span><Image size={17} /></span>
                  <div><h2>{t('menus.customize.style.cardsMedia')}</h2><p>{t('menus.customize.style.cardsMediaDescription')}</p></div>
                </div>
                <div className="menu-customize-select-grid">
                  {[
                    ['card_style', t('menus.customize.style.cardStyle'), 'elevated', [
                      ['flat', t('menus.customize.style.flat')], ['bordered', t('menus.customize.style.bordered')], ['elevated', t('menus.customize.style.elevated')], ['glass', t('menus.customize.style.glass')],
                    ]],
                    ['card_shadow', translateFallback(t, 'menus.customize.style.cardShadow', 'Card shadow'), 'soft', [
                      ['none', translateFallback(t, 'menus.customize.style.shadowNone', 'None')],
                      ['soft', translateFallback(t, 'menus.customize.style.shadowSoft', 'Soft')],
                      ['medium', translateFallback(t, 'menus.customize.style.shadowMedium', 'Medium')],
                      ['strong', translateFallback(t, 'menus.customize.style.shadowStrong', 'Strong')],
                    ]],
                    ['product_image_ratio', t('menus.customize.style.imageRatio'), '4:3', [
                      ['1:1', t('menus.customize.style.square')], ['4:3', t('menus.customize.style.landscape')], ['3:2', t('menus.customize.style.wide')], ['16:9', t('menus.customize.style.cinematic')],
                    ]],
                    ['category_style', t('menus.customize.style.categoryStyle'), 'pills', [
                      ['pills', t('menus.customize.style.pills')], ['tabs', t('menus.customize.style.tabs')], ['underline', t('menus.customize.style.underline')],
                    ]],
                    ['product_density', t('menus.customize.style.productDensity'), 'comfortable', [
                      ['compact', t('menus.customize.style.compact')], ['comfortable', t('menus.customize.style.comfortable')], ['spacious', t('menus.customize.style.spacious')],
                    ]],
                    ['border_radius', t('menus.customize.style.cardRadius'), 'large', [
                      ['small', t('menus.customize.style.small')], ['medium', t('menus.customize.style.medium')], ['large', t('menus.customize.style.large')], ['pill', t('menus.customize.style.veryRound')],
                    ]],
                  ].map(([key, label, fallback, options]) => (
                    <label key={key}>
                      <span>{label}</span>
                      <CustomSelect
                        value={customSettings[key] || templateConfig[key] || fallback}
                        options={options.map(([value, optionLabel]) => ({
                          value,
                          label: optionLabel,
                        }))}
                        onChange={(value) => updateCustomSetting(key, value)}
                      />
                    </label>
                  ))}
                </div>
              </section>
            </>
          )}
          {activePanel === 'type' && (
            <section className="menu-customize-panel">
              <div className="menu-customize-panel__title">
                <span><Type size={17} /></span>
                <div><h2>{t('menus.customize.type.typography')}</h2><p>{t('menus.customize.type.description')}</p></div>
              </div>
              <div className="menu-customize-font-list">
                {[
                  ['system', t('menus.customize.type.system')],
                  ['modern', t('menus.customize.type.modern')],
                  ['rounded', t('menus.customize.type.rounded')],
                  ['serif', t('menus.customize.type.serif')],
                ].map(([value, label]) => (
                  <button key={value} type="button" data-font={value} className={(customSettings.font_family || templateConfig.font_family || 'system') === value ? 'is-active' : ''} onClick={() => updateCustomSetting('font_family', value)}><span>Aa</span><strong>{LANGUAGE_NAMES[value]}</strong></button>
                ))}
              </div>
              <label className="menu-customize-range">
                <span>{t('menus.customize.type.headingScale')} <b>{customSettings.heading_scale || templateConfig.heading_scale || 100}%</b></span>
                <input type="range" min="80" max="145" step="5" value={customSettings.heading_scale || templateConfig.heading_scale || 100} onChange={(event) => updateCustomSetting('heading_scale', Number(event.target.value))} />
              </label>
              <label className="menu-customize-range">
                <span>{t('menus.customize.type.bodyScale')} <b>{customSettings.body_scale || templateConfig.body_scale || 100}%</b></span>
                <input type="range" min="80" max="130" step="5" value={customSettings.body_scale || templateConfig.body_scale || 100} onChange={(event) => updateCustomSetting('body_scale', Number(event.target.value))} />
              </label>
            </section>
          )}
          {activePanel === 'language' && (
            <section className="menu-customize-panel">
              <div className="menu-customize-panel__title">
                <span><Languages size={17} /></span>
                <div>
                  <h2>{t('menus.customize.language.previewLanguage')}</h2>
                  <p>
                    {t('menus.customize.language.description')}
                  </p>
                </div>
              </div>
              <div className="menu-customize-language-list">
                {enabledLanguages.map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={previewLanguage === value ? 'is-active' : ''}
                    onClick={() => setPreviewLanguage(value)}
                  >
                    <span>{value.toUpperCase()}</span>
                    <strong>{LANGUAGE_NAMES[value]}</strong>
                    {previewLanguage === value && <Check size={15} />}
                  </button>
                ))}
              </div>
              <div className="menu-customize-language-note">
                <Languages size={16} />
                <p>
                  {t('menus.customize.language.note')}
                </p>
              </div>
            </section>
          )}
          {activePanel === 'elements' && (
            <section className="menu-customize-panel">
              <div className="menu-customize-panel__title">
                <span><Eye size={17} /></span>
                <div><h2>{t('menus.customize.elements.visibleElements')}</h2><p>{t('menus.customize.elements.description')}</p></div>
              </div>
              <div className="menu-customize-toggle-list">
                {[
                  ['show_images', t('menus.customize.elements.showImages')],
                  ['show_search', t('menus.customize.elements.showSearch')],
                  ['show_category_tabs', t('menus.customize.elements.showCategories')],
                  ['show_category_sections', t('menus.customize.elements.showCategorySections')],
                  ['show_subcategories', t('menus.customize.elements.showSubcategories')],
                  ['show_featured_section', t('menus.customize.elements.showFeatured')],
                  ['show_product_description', t('menus.customize.elements.showDescriptions')],
                  ['show_engagement', t('menus.customize.elements.showRatings')],
                  ['show_restaurant_info', t('menus.customize.elements.showRestaurantInfo')],
                  ['ordering_enabled', t('menus.customize.elements.enableOrdering')],
                  ['show_ordering_badge', t('menus.customize.elements.showOrderingBadge')],
                ].map(([key, label]) => (
                  <label key={key} className="menu-customize-toggle">
                    <span>{label}</span>
                    <input type="checkbox" checked={getBooleanSettingValue(key)} onChange={(event) => updateCustomSetting(key, event.target.checked)} />
                    <span className="menu-customize-switch"><span /></span>
                  </label>
                ))}
              </div>
            </section>
          )}
          <button type="button" className="menu-customize-reset-button menu-customize-reset-button--global" onClick={resetCustomSettings}>
            <RotateCcw size={16} />
            {t('menus.customize.reset')}
          </button>
          </div>
          )}
        </aside>
        <section className="menu-customize-preview-column">
          <div className="menu-customize-preview-header">
            <div>
              <p>
                {translateFallback(
                  t,
                  'menus.customize.previewEyebrow',
                  'Live preview',
                )}
              </p>
              <h2>
                {getTranslationValue(
                  activeTemplate,
                  'name',
                  language,
                ) ||
                  translateFallback(
                    t,
                    'menus.customize.previewTitle',
                    'Menu preview',
                  )}
              </h2>
            </div>
            <div className="menu-customize-preview-header__controls">
              <label className="menu-customize-preview-filter">
                <Search size={16} />
                <input
                  type="search"
                  value={
                    previewSearchTerm
                  }
                  placeholder={translateFallback(
                    t,
                    'menus.customize.previewSearch',
                    'Filter preview...',
                  )}
                  onChange={(event) =>
                    setPreviewSearchTerm(
                      event.target.value,
                    )
                  }
                />
              </label>
              <div className="menu-customize-zoom-control">
                <button
                  type="button"
                  onClick={() =>
                    {
                      setIsPreviewZoomManual(true);
                      setPreviewZoom((current) =>
                        Math.max(0.28, Number((current - 0.1).toFixed(2))),
                      );
                    }
                  }
                  aria-label="Zoom out"
                >
                  <ZoomOut size={16} />
                </button>
                <button
                  type="button"
                  className="menu-customize-zoom-value"
                  onClick={() => {
                    setIsPreviewZoomManual(false);
                    fitPreviewToCanvas();
                  }}
                  title="Fit to screen"
                >
                  {Math.round(previewZoom * 100)}%
                </button>
                <button
                  type="button"
                  onClick={() =>
                    {
                      setIsPreviewZoomManual(true);
                      setPreviewZoom((current) =>
                        Math.min(1.25, Number((current + 0.1).toFixed(2))),
                      );
                    }
                  }
                  aria-label="Zoom in"
                >
                  <ZoomIn size={16} />
                </button>
              </div>
            </div>
          </div>
          <div
            ref={previewStageRef}
            className="menu-customize-preview-stage"
            data-menu-layout={customSettings.layout || templateConfig.layout || 'classic'}
            data-menu-theme={customSettings.theme || templateConfig.theme || 'light'}
            data-card-shadow={customSettings.card_shadow || templateConfig.card_shadow || 'soft'}
            data-logo-position={customSettings.logo_position || templateConfig.logo_position || 'top-right'}
            data-header-align={customSettings.header_alignment || templateConfig.header_alignment || 'left'}
            data-header-layout={customSettings.header_layout || templateConfig.header_layout || 'classic'}
            data-card-style={customSettings.card_style || templateConfig.card_style || 'elevated'}
            data-image-ratio={customSettings.product_image_ratio || templateConfig.product_image_ratio || '4:3'}
            data-category-style={customSettings.category_style || templateConfig.category_style || 'pills'}
            data-font-family={customSettings.font_family || templateConfig.font_family || 'system'}
            data-show-restaurant-info={getBooleanSettingValue('show_restaurant_info') ? 'yes' : 'no'}
            data-show-ordering-badge={getBooleanSettingValue('show_ordering_badge') ? 'yes' : 'no'}
            style={{
              '--custom-logo-size': `${customSettings.logo_size || templateConfig.logo_size || 72}px`,
              '--custom-content-width': `${customSettings.content_width || templateConfig.content_width || 1100}px`,
              '--custom-section-gap': `${customSettings.section_gap || templateConfig.section_gap || 24}px`,
              '--custom-heading-scale': (customSettings.heading_scale || templateConfig.heading_scale || 100) / 100,
              '--custom-body-scale': (customSettings.body_scale || templateConfig.body_scale || 100) / 100,
              '--custom-surface': customSettings.surface_color || templateConfig.surface_color || '#ffffff',
              '--custom-accent': customSettings.accent_color || templateConfig.accent_color || '#D7E3E0',
              '--preview-zoom': previewZoom,
              '--preview-scaled-width': `${Math.ceil(PREVIEW_PHONE_WIDTH * previewZoom)}px`,
              '--preview-scaled-height': `${Math.ceil(PREVIEW_PHONE_HEIGHT * previewZoom)}px`,
            }}
          >
            <div className="menu-customize-preview-zoom-frame">
              <div className="menu-customize-preview-pan-surface">
                <div className="menu-customize-preview-scale-layer">
                  <div className="menu-customize-public-phone">
                    <PublicMenuExperience
                      mode="preview"
                      phonePreview
                      menu={{
                        ...menu,
                        ...(previewData?.menu || {}),
                        design_settings: effectiveConfig,
                      }}
                      establishment={establishment}
                      categories={categories}
                      products={products}
                      featuredProducts={featuredProducts}
                      template={activeTemplate}
                      designSettings={effectiveConfig}
                      orderingEnabled={previewOrderingEnabled}
                      language={previewLanguage}
                      onLanguageChange={(locale) => {
                          if (enabledLanguages.includes(locale)) setPreviewLanguage(locale);
                        }}
                      externalSearchTerm={previewSearchTerm}
                      className="menu-customize-live-preview"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default MenuCustomize;
